import React from 'react';
import { motion } from 'framer-motion';
import { X, FilePlus2, CopyCheck, ChevronRight } from 'lucide-react';

interface Props {
  /** Classe documento su cui si sta lavorando, mostrata in intestazione. */
  documentLabel: string;
  /** Quante fonti attive verrebbero importate: se zero, l'import non ha senso. */
  fontiAttive: number;
  onClose: () => void;
  /** Disattiva tutte le fonti attive e riparte da zero. */
  onListaVuota: () => void;
  /** Apre la selezione di quali fonti conservare. */
  onImporta: () => void;
}

/**
 * Scelta di come iniziare una nuova versione della lista fonti.
 *
 * Sostituisce la `Swal.fire` con `showDenyButton`: tre bottoni affiancati non
 * dicevano quale fosse l'opzione distruttiva, e "Lista vuota" disattiva tutte
 * le fonti esistenti.
 */
const NewVersionModal: React.FC<Props> = ({
  documentLabel,
  fontiAttive,
  onClose,
  onListaVuota,
  onImporta,
}) => {
  const Scelta: React.FC<{
    Icon: React.ComponentType<{ className?: string }>;
    titolo: string;
    testo: string;
    tinta: string;
    bolla: string;
    onClick: () => void;
    disabilitata?: boolean;
  }> = ({ Icon, titolo, testo, tinta, bolla, onClick, disabilitata }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabilitata}
      className="w-full flex items-center gap-3 p-3 rounded-xl border border-borderDivider bg-white text-left transition-all hover:border-textPrimary/30 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-borderDivider disabled:hover:shadow-none"
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${bolla}`}>
        <Icon className={`w-4 h-4 ${tinta}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-textPrimary">{titolo}</div>
        <div className="text-[11px] text-textMuted mt-0.5">{testo}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-textMuted shrink-0" />
    </button>
  );

  return (
    <div className="fixed inset-0 bg-modalOverlay flex items-center justify-center z-[70] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="bg-surface rounded-2xl shadow-xl w-full max-w-md p-5 flex flex-col gap-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-textPrimary">Crea nuova versione</h3>
            <p className="text-[11px] text-textMuted truncate mt-0.5">{documentLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-textMuted hover:text-textPrimary transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <Scelta
            Icon={CopyCheck}
            titolo="Importa la lista precedente"
            testo={
              fontiAttive > 0
                ? `Parti dalle ${fontiAttive} fonti attive e scegli quali conservare.`
                : 'Non ci sono fonti attive da importare.'
            }
            bolla="bg-gray-50"
            tinta="text-textPrimary/60"
            onClick={onImporta}
            disabilitata={fontiAttive === 0}
          />

          <Scelta
            Icon={FilePlus2}
            titolo="Riparti da una lista vuota"
            testo={
              fontiAttive > 0
                ? `Disattiva tutte le ${fontiAttive} fonti attive: l’operazione non si annulla.`
                : 'La lista è già vuota.'
            }
            bolla="bg-amber-50"
            tinta="text-amber-600"
            onClick={onListaVuota}
          />
        </div>

        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-medium bg-buttonPrimaryBg text-buttonPrimaryText hover:bg-buttonPrimaryHover transition-colors"
          >
            Annulla
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default NewVersionModal;
